/**
 * Club sport: one required field, three creation paths, two read surfaces.
 *
 * `clubs.sport_id` has existed since the migration that moved sport up from
 * team to club, but nothing ever validated it, so most clubs carry `null`.
 * That matters because the per-sport position catalogue resolves
 * club -> sport -> positions: a club with no sport gives its players no
 * positions at all. The API now rejects a missing, blank or unknown
 * `sport_id` on every write path (`POST /admin/clubs`, club registration and
 * onboarding) while deliberately still serving — and updating — the rows that
 * predate the rule.
 *
 * This module is the single place the frontend agrees with that: one zod
 * field the three creation forms share, one wording for "why is this
 * required", and one renderer for a club whose sport is still null. Pure, so
 * the wording and the null-handling are unit-testable without a form.
 */
import { z } from "zod";

/**
 * Shown when a club is saved without a sport.
 *
 * Deliberately not "Sport is required": the client's complaint that produced
 * this rule was that nobody knew what the field was *for*. The message names
 * the consequence instead, because on the edit form it is the only
 * explanation a user gets for why a club they have edited happily for months
 * suddenly refuses to save.
 */
export const CLUB_SPORT_REQUIRED_MESSAGE =
  "Select the sport this club plays — it decides which positions its players can be given.";

/**
 * Shown on the edit form when the club being edited loaded with no sport.
 * The block is the point (the API will reject the save), so the hint says so
 * up front rather than letting the user discover it on submit.
 */
export const CLUB_SPORT_BACKFILL_HINT =
  "This club was created before a sport was required. Pick one to save your changes — until then its players have no positions to choose from.";

/** Rendered in place of a sport name for a club that has none. */
export const CLUB_SPORT_MISSING_LABEL = "Not set";

/**
 * Rendered when a club has a `sport_id` but no `sport_name` — the sport was
 * deleted, or the read did not resolve the relation. Distinct from
 * {@link CLUB_SPORT_MISSING_LABEL} so "nobody chose a sport" is never
 * confused with "the chosen sport could not be read".
 */
export const CLUB_SPORT_UNKNOWN_LABEL = "Unknown sport";

/**
 * The required `sport_id` field, shared by the admin create form, the admin
 * edit form and (as `clubSportId`) the club registration and onboarding
 * schemas — so all four fail with the same sentence.
 *
 * The forms hold `""` rather than `null` for "nothing picked yet", which is
 * what makes a plain `min(1)` the whole rule; `trim` covers a value that
 * arrives as whitespace. The `error` argument covers the remaining shapes —
 * without it a `null` loaded from a pre-rule club would report zod's
 * "expected string, received null" instead of the sentence above.
 */
export const clubSportIdSchema = z
  .string({ error: CLUB_SPORT_REQUIRED_MESSAGE })
  .trim()
  .min(1, CLUB_SPORT_REQUIRED_MESSAGE);

/** The sport fields as they appear on every `/admin/clubs` payload. */
export type TClubSportSource = {
  sport_id?: string | null;
  sport_name?: string | null;
};

export type TClubSportDisplay = {
  /** Always a non-empty string — never `""`, never `"undefined"`. */
  label: string;
  /**
   * True when no sport name could be shown, for either reason. Callers use
   * it to mute the text or mark the badge; it is a display concern only and
   * must never gate the read itself.
   */
  isMissing: boolean;
};

/**
 * How to render a club's sport, including the six-of-seven case where there
 * isn't one.
 *
 * Never throws and never returns an empty label: reads of pre-rule clubs have
 * to keep working, so an absent sport degrades to a labelled placeholder
 * rather than a blank cell or a crash.
 */
export function clubSportDisplay(
  club: TClubSportSource | null | undefined
): TClubSportDisplay {
  const name = club?.sport_name?.trim();
  if (name) return { label: name, isMissing: false };

  const id = club?.sport_id?.trim();
  if (id) return { label: CLUB_SPORT_UNKNOWN_LABEL, isMissing: true };

  return { label: CLUB_SPORT_MISSING_LABEL, isMissing: true };
}

/**
 * Whether the edit form should show {@link CLUB_SPORT_BACKFILL_HINT}, i.e.
 * the club was loaded without a sport and the user has not picked one yet.
 */
export function needsClubSportBackfill(
  loadedSportId: string | null | undefined,
  currentValue: string | null | undefined
): boolean {
  const hadSport = !!loadedSportId?.trim();
  const hasPicked = !!currentValue?.trim();
  return !hadSport && !hasPicked;
}
